/**
 * Where a care-agent nudge (push category `proactive_gap`) opens.
 *
 * Mirrors the backend's _gapUrl (services/proactive-prompts), which the SMS
 * and email "View" link use: a nudge about a finding opens that finding on
 * the care notes screen (AgentSuggestions), a missing or incomplete contact
 * opens the care team, a nudge that carries initial_prompt opens the plan's
 * chat asking Ellie it, and anything else opens the plan's chat with the
 * question as context, as every nudge did before.
 */

import type { PendingNotificationNav } from './pendingNotificationNav';

const CONTACT_GAPS = new Set(['missing_contacts', 'incomplete_contacts']);

function decode(value: unknown): string | undefined {
  if (value == null) return undefined;
  const str = String(value);
  try { return decodeURIComponent(str); } catch { return str; }
}

function positiveInt(value: unknown): number | null {
  const n = Number(value);
  return Number.isSafeInteger(n) && n > 0 ? n : null;
}

export function proactiveGapDestination(data: Record<string, unknown> | null | undefined): PendingNotificationNav | null {
  const projectId = positiveInt(data?.project_id);
  if (!data || projectId == null) return null;

  const careNoteId = positiveInt(data.note_id);
  if (careNoteId != null) return { projectId, screen: 'AgentSuggestions', careNoteId };
  if (CONTACT_GAPS.has(String(data.gap_type || ''))) return { projectId, screen: 'ProjectContacts', tab: 'contacts' };
  // A nudge with no finding behind it (a vitals change) carries the question
  // to ask Ellie, so the chat opens on her answer rather than a reply box.
  const initialPrompt = decode(data.initial_prompt);
  if (initialPrompt) return { projectId, screen: 'ProjectChat', initialPrompt };
  return {
    projectId,
    screen: 'ProjectChat',
    proactiveGapContext: decode(data.prompt_text) || decode(data.body),
  };
}
